import Page from "components/Page";
import React, { useCallback, useState } from "react";
import styles from "./../aisettings.module.scss";
import { withTranslation } from "react-i18next";
import constants from "./../../../../constants";
import Text from "components/Text";
import Table from "components/table";
import Empty from "components/Empty";
import moment from "moment";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import {
  deletePrompt,
  useGetPromptsList,
} from "repositories/ai-assistant-repository";
import useHandleApiError from "hooks/useHandleApiError";
import { encodeId, formatLocalDateTime } from "utils";

const PAGE_SIZE = constants.pageSize;

function ManagePrompts({ history, t }) {
  const [pageNumber, setPageNumber] = useState(1);
  const [confirmDelete, setConfirmDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const {
    isLoading,
    data,
    refetch,
    isFetching,
    error: isError,
  } = useGetPromptsList(pageNumber, PAGE_SIZE, searchTerm);
  useHandleApiError(isLoading, isFetching, isError);

  const goBack = () =>
    history.push({ pathname: constants.routes.aiAssistant.aiSetting });

  const columns = [
    {
      attrs: { datatitle: t("aiAssistant.managePrompts.promptName") },
      dataField: "promptName",
      text: t("aiAssistant.managePrompts.promptName"),
      formatter: (cellContent) => {
        if (!isLoading) return cellContent;
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.description") },
      dataField: "description",
      text: t("aiAssistant.description"),
      formatter: (cellContent) => {
        if (!isLoading)
          return (
            <span className="truncate_text">
              {cellContent ? cellContent : "--"}
            </span>
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.uploadedOn") },
      dataField: "createdAt",
      text: t("aiAssistant.uploadedOn"),
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent
            ? formatLocalDateTime(cellContent, "MMM DD, YYYY")
            : "--";
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      formatter: (cellContent, row) => {
        if (isLoading)
          return <div className="text-placeholder-100 shimmer-animation"></div>;
        return (
          <>
            <span
              className="pointer mr-4"
              onClick={() => handleRedirectionWithData(row?.id, true)}
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "rgb(88, 126, 133)",
              }}
            >
              {" "}
              <u>{t("View")}</u>
            </span>
            <span
              className="pointer mr-4"
              onClick={() => handleRedirectionWithData(row?.id, false)}
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "rgb(88, 126, 133)",
              }}
            >
              {" "}
              <u>{t("Edit")}</u>
            </span>
            <span
              className="pointer"
              onClick={() => deleteAIDocument(row?.id)}
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#e76f2a",
              }}
            >
              {" "}
              <u>{t("Delete")}</u>
            </span>
          </>
        );
      },
    },
  ];

  const handleSearchFilter = (event) => {
    if (event.target.value?.trim()) {
      setSearchTerm(event.target.value);
    } else {
      setSearchTerm("");
    }
  };

  const optimisedSearch = useCallback(debounce(handleSearchFilter, 1000));

  const handleRedirectionWithData = async (promptId, isViewDetailMode) => {
    const pathname = isViewDetailMode
      ? constants.routes.aiAssistant.viewPrompt.replace(
          ":promptId",
          encodeId(promptId),
        )
      : constants.routes.aiAssistant.editPrompt.replace(
          ":promptId",
          encodeId(promptId),
        );

    const selectedData = data?.items?.find((val) => val?.id == promptId);
    history.push({
      pathname,
      state: {
        selectedData,
      },
    });
  };

  const deleteAIDocument = (documentId) => {
    setSelectedId(documentId);
    setConfirmDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    try {
      const resp = await deletePrompt(selectedId);
      setConfirmDeleteModal(false);
      toast.success(resp?.message);
      refetch();
    } catch (e) {
      setConfirmDeleteModal(false);
      toast.error(e.message);
    }
  };

  const closeDeleteModal = () => {
    setConfirmDeleteModal(false);
  };

  let rows = [];
  let totalItems = 0;
  if (!isLoading && data && data?.items) {
    rows = data.items;
    totalItems = data?.totalCount;
  } else {
    rows = new Array(PAGE_SIZE).fill({});
    totalItems = PAGE_SIZE;
  }

  return (
    <>
      <Page
        onBack={() => goBack()}
        className={styles["global-main-page"]}
        title={t("aiAssistant.managePrompt")}
      >
        <Text size="14px" weight="300" color="#000000">
          {t("aiAssistant.aiAssistSubheading")}
        </Text>
        <div className="d-flex justify-content-between mb-4 mt-4">
          <div className="search-box ">
            <input
              name="searchTerm"
              type="text"
              placeholder={t("aiAssistant.search")}
              onInput={optimisedSearch}
            />
            <span className="ico">
              <img
                src={require("assets/images/search-icon.svg").default}
                alt="icon"
              />
            </span>
          </div>
          <div className={styles["upload-document-btn"]}>
            <button
              onClick={() =>
                history.push(constants.routes.aiAssistant.addPrompt)
              }
              className="button  button-round button-shadow"
            >
              {t("aiAssistant.managePrompts.addNewPrompt")}
            </button>
          </div>
        </div>
        <div className="data-list">
          <Table
            columns={columns}
            data={rows}
            keyField="id"
            handlePagination={setPageNumber}
            pageNumber={pageNumber}
            totalItems={totalItems}
            pageSize={PAGE_SIZE}
          />
          {!isLoading && totalItems === 0 && (
            <Empty
              Message={t("aiAssistant.noRecord", {
                item: t("aiAssistant.prompt"),
              })}
            />
          )}
        </div>
        {confirmDelete && (
          <DeleteConfirmationModal
            closeDeleteModal={closeDeleteModal}
            confirmDelete={confirmDelete}
            confirmDeleteMessage={confirmDeleteMessage}
            deleteMessage={t("aiAssistant.managePrompts.deleteMessage")}
            title={t("aiAssistant.managePrompts.deleteModalTitle")}
            btnText={t("delete")}
          />
        )}
      </Page>
    </>
  );
}

export default withTranslation()(ManagePrompts);
